import { useState } from 'react';
import { EChart as ReactEChartsCore } from './EChart';
import * as echarts from 'echarts/core';
import { BarChart as EChartsBar } from 'echarts/charts';
import { AriaComponent, GridComponent, TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

import { LABOUR_BY_TRADE } from '../../data/fixtures';
import { formatNumber } from '../../i18n/format';
import { ChartFrame } from './ChartFrame';
import { ChartLegend, type ChartLegendItem } from './ChartLegend';
import {
  axisTokens,
  entranceAnimation,
  formatMonth,
  seriesColor,
  tooltipTokens,
  useChartMotion,
  useChartTheme,
} from './ChartTheme';

echarts.use([EChartsBar, AriaComponent, GridComponent, TooltipComponent, CanvasRenderer]);

type LabourRow = (typeof LABOUR_BY_TRADE)[number];
type LabourMonth = Exclude<keyof LabourRow, 'trade'>;

/**
 * Typed against the fixture's own keys, so renaming or adding a month column in
 * `LABOUR_BY_TRADE` fails the type check here instead of silently charting
 * `undefined`.
 */
const MONTHS: readonly LabourMonth[] = ['2026-07', '2026-08', '2026-09'];

const count = (value: number) => formatNumber(value);

export function LabourChart() {
  const theme = useChartTheme();
  const motion = useChartMotion(theme);
  const [hidden, setHidden] = useState<string[]>([]);

  const axis = axisTokens(theme);
  const trades = LABOUR_BY_TRADE.map((row) => row.trade);

  const legendItems: ChartLegendItem[] = MONTHS.map((month, index) => ({
    id: month,
    label: formatMonth(month),
    color: seriesColor(theme, index),
    value: count(LABOUR_BY_TRADE.reduce((total, row) => total + row[month], 0)),
  }));

  const series = MONTHS.filter((month) => !hidden.includes(month)).map((month) => ({
    id: month,
    name: formatMonth(month),
    type: 'bar',
    itemStyle: { color: seriesColor(theme, MONTHS.indexOf(month)) },
    data: LABOUR_BY_TRADE.map((row) => row[month]),
    animationDelay: motion.delay,
  }));

  const total = LABOUR_BY_TRADE.reduce(
    (rowsTotal, row) => rowsTotal + MONTHS.reduce((rowTotal, month) => rowTotal + row[month], 0),
    0,
  );

  return (
    <ChartFrame
      title="Labour by trade"
      description={`Hours logged per trade across ${MONTHS.length} months.`}
      summary={`Hours logged per trade for ${LABOUR_BY_TRADE.length} trades over ${
        MONTHS.length
      } months, ${count(total)} hours in total.`}
      chartLabel={`Grouped bar chart of hours logged per trade over ${MONTHS.length} months`}
      legend={<ChartLegend label="Months" items={legendItems} hidden={hidden} onToggle={(id) => setHidden((prev) => (prev.includes(id) ? prev.filter((entry) => entry !== id) : [...prev, id]))} />}
      empty={series.length === 0 ? 'All months are hidden. Switch one back on in the legend.' : undefined}
      table={{
        caption: 'Hours logged per trade, by month',
        columns: ['Trade', ...MONTHS.map(formatMonth), 'Total'],
        rows: LABOUR_BY_TRADE.map((row) => [
          row.trade,
          ...MONTHS.map((month) => count(row[month])),
          count(MONTHS.reduce((rowTotal, month) => rowTotal + row[month], 0)),
        ]),
      }}
    >
      <ReactEChartsCore
        echarts={echarts}
        style={{ width: '100%', height: '100%' }}
        option={{
          ...entranceAnimation(motion),
          aria: { enabled: true, label: { enabled: false }, decal: { show: true } },
          grid: { left: 4, right: 16, top: 16, bottom: 4, containLabel: true },
          tooltip: { trigger: 'axis', ...tooltipTokens(theme, count), axisPointer: { type: 'shadow' } },
          xAxis: { type: 'category', data: trades, ...axis, splitLine: { show: false } },
          yAxis: { type: 'value', ...axis, axisLabel: { ...axis.axisLabel, formatter: count } },
          series,
        }}
      />
    </ChartFrame>
  );
}
